import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { useToast } from '../hooks/use-toast';
import { 
  ClipboardCheck, Plus, Search, Filter, AlertTriangle, Clock, 
  CheckCircle2, XCircle, User, Calendar, FileText, TrendingUp,
  ChevronRight, Loader2, Target, Shield, AlertCircle, ArrowRight,
  Link2, History, Star, Download
} from 'lucide-react';
import { Progress } from './ui/progress';
import ExportButton from './ExportButton';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700', icon: FileText },
  open: { label: 'Open', color: 'bg-blue-100 text-blue-700', icon: AlertCircle },
  in_progress: { label: 'In Progress', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  pending_review: { label: 'Pending Review', color: 'bg-purple-100 text-purple-700', icon: Target },
  pending_verification: { label: 'Pending Verification', color: 'bg-orange-100 text-orange-700', icon: Shield },
  verified: { label: 'Verified', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  closed: { label: 'Closed', color: 'bg-slate-100 text-slate-700', icon: CheckCircle2 },
  overdue: { label: 'Overdue', color: 'bg-red-100 text-red-700', icon: AlertTriangle }
};

const PRIORITY_CONFIG = {
  critical: { label: 'Critical', color: 'bg-red-500 text-white' },
  high: { label: 'High', color: 'bg-orange-500 text-white' },
  medium: { label: 'Medium', color: 'bg-yellow-500 text-white' },
  low: { label: 'Low', color: 'bg-green-500 text-white' }
};

const SOURCE_OPTIONS = [
  { value: 'incident', label: 'Incident' },
  { value: 'near_miss', label: 'Near Miss' },
  { value: 'hazard', label: 'Hazard' },
  { value: 'audit', label: 'Audit' },
  { value: 'inspection', label: 'Inspection' },
  { value: 'observation', label: 'Observation' },
  { value: 'regulatory', label: 'Regulatory' },
  { value: 'proactive', label: 'Proactive' }
];

const CAPAPage = () => {
  const { toast } = useToast();
  const [capas, setCapas] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCapa, setSelectedCapa] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [filter, setFilter] = useState({ status: '', priority: '', source: '' });
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    capa_type: 'corrective',
    priority: 'medium',
    source: 'incident',
    source_id: '',
    department: '',
    location: '',
    assigned_to: '',
    due_date: '',
    verification_required: true
  });

  useEffect(() => {
    fetchData();
  }, [filter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.status) params.append('status', filter.status);
      if (filter.priority) params.append('priority', filter.priority);
      if (filter.source) params.append('source', filter.source);
      
      const [capasRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/capa?${params.toString()}`),
        axios.get(`${API}/api/capa/stats`)
      ]);
      
      setCapas(capasRes.data.capas || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching CAPA data:', error);
      toast({ title: 'Error', description: 'Failed to load CAPA data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCapa = async () => {
    if (!formData.title || !formData.description || !formData.due_date) {
      toast({ title: 'Error', description: 'Please fill in required fields', variant: 'destructive' });
      return;
    }
    
    try {
      const response = await axios.post(`${API}/api/capa`, formData);
      toast({ title: 'Success', description: `CAPA ${response.data.capa_number} created` });
      setShowCreateDialog(false);
      setFormData({
        title: '', description: '', capa_type: 'corrective', priority: 'medium',
        source: 'incident', source_id: '', department: '', location: '',
        assigned_to: '', due_date: '', verification_required: true
      });
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to create CAPA', variant: 'destructive' });
    }
  };

  const handleStatusChange = async (capaId, newStatus) => {
    try {
      await axios.patch(`${API}/api/capa/${capaId}`, { status: newStatus });
      toast({ title: 'Updated', description: `Status changed to ${newStatus}` });
      fetchData();
      if (selectedCapa?.id === capaId) {
        setSelectedCapa({ ...selectedCapa, status: newStatus });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update status', variant: 'destructive' });
    }
  };

  const handleActionUpdate = async (capaId, actionType, actionId, updates) => {
    try {
      await axios.patch(`${API}/api/capa/${capaId}/actions/${actionType}/${actionId}`, updates);
      toast({ title: 'Updated', description: 'Action item updated' });
      // Refresh selected CAPA
      const res = await axios.get(`${API}/api/capa/${capaId}`);
      setSelectedCapa(res.data);
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update action', variant: 'destructive' });
    }
  };

  const viewCapaDetails = async (capa) => {
    try {
      const res = await axios.get(`${API}/api/capa/${capa.id}`);
      setSelectedCapa(res.data);
      setShowDetailDialog(true);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load CAPA details', variant: 'destructive' });
    }
  };

  const getProgressPercentage = (capa) => {
    const corrective = capa.corrective_actions || [];
    const preventive = capa.preventive_actions || [];
    const total = corrective.length + preventive.length;
    if (total === 0) return 0;
    const completed = [...corrective, ...preventive].filter(a => a.status === 'completed').length;
    return Math.round((completed / total) * 100);
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6" data-testid="capa-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ClipboardCheck className="w-7 h-7 text-blue-600" />
            CAPA Management
          </h1>
          <p className="text-slate-500">Corrective and Preventive Action Tracking</p>
        </div>
        <div className="flex items-center gap-2">
          <ExportButton dataType="capa" label="Export" />
          <Button onClick={() => setShowCreateDialog(true)} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" /> New CAPA
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total CAPAs</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.total || 0}</p>
                </div>
                <ClipboardCheck className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Open</p>
                  <p className="text-2xl font-bold text-blue-600">{(stats.by_status?.open || 0) + (stats.by_status?.in_progress || 0)}</p>
                </div>
                <AlertCircle className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Overdue</p>
                  <p className="text-2xl font-bold text-red-600">{stats.overdue || 0}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Critical Open</p>
                  <p className="text-2xl font-bold text-orange-600">{stats.open_critical || 0}</p>
                </div>
                <Target className="w-8 h-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Avg. Closure</p>
                  <p className="text-2xl font-bold text-green-600">{stats.avg_closure_days || 0}d</p>
                </div>
                <TrendingUp className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-500" />
              <span className="text-sm text-slate-600">Filters:</span>
            </div>
            <Select value={filter.status || "all"} onValueChange={v => setFilter({...filter, status: v === "all" ? "" : v})}>
              <SelectTrigger className="w-40"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filter.priority || "all"} onValueChange={v => setFilter({...filter, priority: v === "all" ? "" : v})}>
              <SelectTrigger className="w-40"><SelectValue placeholder="Priority" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priority</SelectItem>
                {Object.entries(PRIORITY_CONFIG).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filter.source || "all"} onValueChange={v => setFilter({...filter, source: v === "all" ? "" : v})}>
              <SelectTrigger className="w-40"><SelectValue placeholder="Source" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                {SOURCE_OPTIONS.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(filter.status || filter.priority || filter.source) && (
              <Button variant="ghost" size="sm" onClick={() => setFilter({status: '', priority: '', source: ''})}>
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* CAPA List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : capas.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <ClipboardCheck className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No CAPA records found</p>
            <Button className="mt-4" onClick={() => setShowCreateDialog(true)}>
              <Plus className="w-4 h-4 mr-2" /> Create First CAPA
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {capas.map(capa => {
            const StatusIcon = STATUS_CONFIG[capa.status]?.icon || AlertCircle;
            const progress = getProgressPercentage(capa);
            const isOverdue = capa.is_overdue || (capa.due_date < new Date().toISOString().split('T')[0] && !['closed', 'verified'].includes(capa.status));
            
            return (
              <Card 
                key={capa.id} 
                className={`hover:shadow-md transition-shadow cursor-pointer ${isOverdue ? 'border-red-300 bg-red-50/50' : ''}`}
                onClick={() => viewCapaDetails(capa)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <Badge className={PRIORITY_CONFIG[capa.priority]?.color}>
                          {PRIORITY_CONFIG[capa.priority]?.label}
                        </Badge>
                        <Badge variant="outline" className={STATUS_CONFIG[capa.status]?.color}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {STATUS_CONFIG[capa.status]?.label}
                        </Badge>
                        {isOverdue && (
                          <Badge className="bg-red-600 text-white">OVERDUE</Badge>
                        )}
                        <span className="text-sm text-slate-500">{capa.capa_number}</span>
                      </div>
                      <h3 className="font-semibold text-slate-900 mb-1">{capa.title}</h3>
                      <p className="text-sm text-slate-600 line-clamp-2 mb-2">{capa.description}</p>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          Due: {capa.due_date}
                        </span>
                        {capa.assigned_to && (
                          <span className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            {capa.assigned_to}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Link2 className="w-4 h-4" />
                          {capa.source}
                        </span>
                      </div>
                    </div>
                    <div className="text-right min-w-[100px]">
                      <div className="text-sm text-slate-500 mb-1">Progress</div>
                      <div className="text-2xl font-bold text-blue-600">{progress}%</div>
                      <Progress value={progress} className="h-2 mt-2" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create CAPA Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create New CAPA</DialogTitle>
            <DialogDescription>Document corrective and preventive actions</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label>Title *</Label>
                <Input 
                  value={formData.title} 
                  onChange={e => setFormData({...formData, title: e.target.value})}
                  placeholder="Brief description of the issue"
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Description *</Label>
                <Textarea 
                  value={formData.description}
                  onChange={e => setFormData({...formData, description: e.target.value})}
                  placeholder="Detailed description of the problem and its impact"
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label>CAPA Type</Label>
                <Select value={formData.capa_type} onValueChange={v => setFormData({...formData, capa_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="corrective">Corrective</SelectItem>
                    <SelectItem value="preventive">Preventive</SelectItem>
                    <SelectItem value="both">Both</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={formData.priority} onValueChange={v => setFormData({...formData, priority: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="critical">Critical</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Source</Label>
                <Select value={formData.source} onValueChange={v => setFormData({...formData, source: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SOURCE_OPTIONS.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Due Date *</Label>
                <Input 
                  type="date"
                  value={formData.due_date}
                  onChange={e => setFormData({...formData, due_date: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Assigned To</Label>
                <Input 
                  value={formData.assigned_to}
                  onChange={e => setFormData({...formData, assigned_to: e.target.value})}
                  placeholder="Person responsible"
                />
              </div>
              <div className="space-y-2">
                <Label>Department</Label>
                <Input 
                  value={formData.department}
                  onChange={e => setFormData({...formData, department: e.target.value})}
                  placeholder="Department"
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Location</Label>
                <Input 
                  value={formData.location}
                  onChange={e => setFormData({...formData, location: e.target.value})}
                  placeholder="Where did the issue occur?"
                />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateCapa}>Create CAPA</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* CAPA Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedCapa && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <DialogTitle className="flex items-center gap-2">
                      {selectedCapa.title}
                    </DialogTitle>
                    <DialogDescription>{selectedCapa.capa_number}</DialogDescription>
                  </div>
                  <div className="flex gap-2">
                    <Badge className={PRIORITY_CONFIG[selectedCapa.priority]?.color}>
                      {PRIORITY_CONFIG[selectedCapa.priority]?.label}
                    </Badge>
                    <Badge className={STATUS_CONFIG[selectedCapa.status]?.color}>
                      {STATUS_CONFIG[selectedCapa.status]?.label}
                    </Badge>
                  </div>
                </div>
              </DialogHeader>
              
              <Tabs defaultValue="details" className="mt-4">
                <TabsList>
                  <TabsTrigger value="details">Details</TabsTrigger>
                  <TabsTrigger value="actions">Actions ({(selectedCapa.corrective_actions?.length || 0) + (selectedCapa.preventive_actions?.length || 0)})</TabsTrigger>
                  <TabsTrigger value="history">History</TabsTrigger>
                </TabsList>
                
                <TabsContent value="details" className="space-y-4">
                  <Card>
                    <CardContent className="p-4 space-y-3">
                      <div>
                        <Label className="text-slate-500">Description</Label>
                        <p className="text-slate-900">{selectedCapa.description}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-slate-500">Source</Label>
                          <p className="text-slate-900 capitalize">{selectedCapa.source}</p>
                        </div>
                        <div>
                          <Label className="text-slate-500">Due Date</Label>
                          <p className="text-slate-900">{selectedCapa.due_date}</p>
                        </div>
                        <div>
                          <Label className="text-slate-500">Assigned To</Label>
                          <p className="text-slate-900">{selectedCapa.assigned_to || 'Unassigned'}</p>
                        </div>
                        <div>
                          <Label className="text-slate-500">Department</Label>
                          <p className="text-slate-900">{selectedCapa.department || 'N/A'}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* Status Actions */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Update Status</CardTitle>
                    </CardHeader>
                    <CardContent className="flex flex-wrap gap-2">
                      {selectedCapa.status === 'open' && (
                        <Button size="sm" onClick={() => handleStatusChange(selectedCapa.id, 'in_progress')}>
                          Start Progress
                        </Button>
                      )}
                      {selectedCapa.status === 'in_progress' && (
                        <Button size="sm" onClick={() => handleStatusChange(selectedCapa.id, 'pending_verification')}>
                          Submit for Verification
                        </Button>
                      )}
                      {selectedCapa.status === 'pending_verification' && (
                        <Button size="sm" className="bg-green-600" onClick={() => handleStatusChange(selectedCapa.id, 'verified')}>
                          Mark Verified
                        </Button>
                      )}
                      {['verified', 'pending_verification'].includes(selectedCapa.status) && (
                        <Button size="sm" variant="outline" onClick={() => handleStatusChange(selectedCapa.id, 'closed')}>
                          Close CAPA
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>
                
                <TabsContent value="actions" className="space-y-4">
                  {/* Corrective Actions */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Target className="w-4 h-4 text-blue-600" />
                        Corrective Actions
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {selectedCapa.corrective_actions?.length > 0 ? (
                        <div className="space-y-2">
                          {selectedCapa.corrective_actions.map((action, i) => (
                            <div key={action.id || i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                              <div className="flex items-center gap-3">
                                <input
                                  type="checkbox"
                                  checked={action.status === 'completed'}
                                  onChange={() => handleActionUpdate(
                                    selectedCapa.id, 
                                    'corrective', 
                                    action.id, 
                                    { status: action.status === 'completed' ? 'pending' : 'completed' }
                                  )}
                                  className="w-5 h-5"
                                />
                                <div>
                                  <p className={action.status === 'completed' ? 'line-through text-slate-500' : 'text-slate-900'}>
                                    {action.description}
                                  </p>
                                  <p className="text-xs text-slate-500">
                                    {action.assigned_to} • Due: {action.due_date}
                                  </p>
                                </div>
                              </div>
                              <Badge variant="outline">{action.status}</Badge>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-slate-500 text-sm">No corrective actions added</p>
                      )}
                    </CardContent>
                  </Card>
                  
                  {/* Preventive Actions */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Shield className="w-4 h-4 text-green-600" />
                        Preventive Actions
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {selectedCapa.preventive_actions?.length > 0 ? (
                        <div className="space-y-2">
                          {selectedCapa.preventive_actions.map((action, i) => (
                            <div key={action.id || i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                              <div className="flex items-center gap-3">
                                <input
                                  type="checkbox"
                                  checked={action.status === 'completed'}
                                  onChange={() => handleActionUpdate(
                                    selectedCapa.id, 
                                    'preventive', 
                                    action.id, 
                                    { status: action.status === 'completed' ? 'pending' : 'completed' }
                                  )}
                                  className="w-5 h-5"
                                />
                                <div>
                                  <p className={action.status === 'completed' ? 'line-through text-slate-500' : 'text-slate-900'}>
                                    {action.description}
                                  </p>
                                  <p className="text-xs text-slate-500">
                                    {action.assigned_to} • Due: {action.due_date}
                                  </p>
                                </div>
                              </div>
                              <Badge variant="outline">{action.status}</Badge>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-slate-500 text-sm">No preventive actions added</p>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>
                
                <TabsContent value="history">
                  <Card>
                    <CardContent className="p-4">
                      {selectedCapa.history?.length > 0 ? (
                        <div className="space-y-3">
                          {selectedCapa.history.map((h, i) => (
                            <div key={i} className="flex items-start gap-3 pb-3 border-b last:border-0">
                              <History className="w-4 h-4 text-slate-400 mt-1" />
                              <div>
                                <p className="text-sm text-slate-900 capitalize">{h.action.replace(/_/g, ' ')}</p>
                                <p className="text-xs text-slate-500">{new Date(h.timestamp).toLocaleString()}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-slate-500 text-sm">No history available</p>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CAPAPage;
